import './CommissionStatementAccessForm.scss';
import { Heading } from '@lmig/lmds-react-typography';
import { Form } from '@lmig/lmds-react-form';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { FieldGroup, Message } from '@lmig/lmds-react-field-group';
import { Button } from '@lmig/lmds-react-button';
import { Textarea } from '@lmig/lmds-react-textarea';
import { useUserData } from 'Foundation/User/UserContext';
import { IconAddItem, IconRemoveItem } from '@lmig/lmds-react-icons';
import { useEffect, useState } from 'react';

type CAForm = {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
  instructions?: string;
  additional_access: { userid?: string; email?: string; name?: string }[];
};

type MJMLTemplate = {
  AgencyName: string;
  AgencyCode: string;
  Name?: string;
  Title?: string;
  Email?: string;
  Phone?: string;
  SpecialInstructions?: string;
  User1?: { Name: string; Email: string; ID: string };
  User2?: { Name: string; Email: string; ID: string };
  User3?: { Name: string; Email: string; ID: string };
  User4?: { Name: string; Email: string; ID: string };
  User5?: { Name: string; Email: string; ID: string };
};

function CommissionAccess() {
  const { userData } = useUserData();
  const [form, setForm] = useState<CAForm>({ additional_access: [{ name: '', email: '', userid: '' }] });
  const [error, setError] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>();
  const [moreErrors, setMoreErrors] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<Message[]>([]);
  const [moreErrorMessages, setMoreErrorMessages] = useState<Message[]>([]);

  const sendEmail = async () => {
    if (userData) {
      setSending(true);
      let mjml: MJMLTemplate = {
        AgencyName: userData.selectedStatCodeData.AgencyName,
        AgencyCode: userData.selectedStatCodeData.SubStat,
        Name: form.name,
        Title: form.role,
        Email: form.email,
        Phone: form.phone,
        SpecialInstructions: form.instructions,
      };

      for (var i = 0; i < form.additional_access.length; i++)
        (mjml as any)[`User${i + 1}`] = {
          Name: form.additional_access[i].name,
          Email: form.additional_access[i].email,
          ID: form.additional_access[i].userid,
        };

      await fetch('/api/sitecore/Messaging/Email', {
        method: 'POST',
        body: new URLSearchParams({
          template: 'commission_statement_authorization.mjml',
          mjml: JSON.stringify(mjml),
          subject: 'Commission report authorization request-LM agent portal',
        }),
      });
      setSending(false);
      setSent(true);
    }
  };

  const requestAccess = () => {
    if (!form.name)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Name is required' }]);
    if (!form.role)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Role is required' }]);
    if (!form.phone)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Phone is required' }]);
    if (!form.email)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Email is required' }]);

    if (form.additional_access && form.additional_access.find((x) => !x.name))
      setMoreErrorMessages((moreErrorMessages) => [...moreErrorMessages, { type: 'error', text: 'Name is required' }]);

    if (form.additional_access && form.additional_access.find((x) => !x.userid))
      setMoreErrorMessages((moreErrorMessages) => [
        ...moreErrorMessages,
        { type: 'error', text: 'User ID is required' },
      ]);

    if (form.additional_access && form.additional_access.find((x) => !x.email))
      setMoreErrorMessages((moreErrorMessages) => [...moreErrorMessages, { type: 'error', text: 'Email is required' }]);

    if (form.additional_access && form.additional_access.find((x) => !x.name || !x.email || !x.userid)) {
      setMoreErrors(true);
    }

    if (!form.name || !form.role || !form.email || !form.phone) {
      setError(true);
    } else if (form.additional_access && !form.additional_access.find((x) => !x.name || !x.email || !x.userid)) {
      sendEmail();
    }
  };

  useEffect(() => {
    if (sent) setSent(false);
    if (error) {
      setErrorMessages([]);
      setError(false);
    }
    if (moreErrors) {
      setMoreErrorMessages([]);
      setMoreErrors(false);
    }
  }, [form]);

  const onNameChange = (event: any) => {
    if (event.currentTarget) setForm((form) => ({ ...form, name: event.currentTarget?.value }));
  };

  const onRoleChange = (event: any) => {
    if (event.currentTarget) setForm((form) => ({ ...form, role: event.currentTarget?.value }));
  };

  const onPhoneChange = (event: any) => {
    if (event.currentTarget) setForm((form) => ({ ...form, phone: event.currentTarget?.value }));
  };

  const onEmailChange = (event: any) => {
    if (event.currentTarget) setForm((form) => ({ ...form, email: event.currentTarget?.value }));
  };

  return (
    <div data-theme="evolved">
      <Form id="commission-statement-access-form">
        <Heading type="h2-light">Commission statement access</Heading>
        <div className="cs-form-box">
          <p className="cs-form-description">Grant access to direct bill commission statements</p>

          {userData && userData.selectedStatCodeData && (
            <div className="cs-form-agency-row">
              {userData.selectedStatCodeData.SubStat && (
                <span>
                  <p className="cs-form-label">Agency code</p>
                  <div>{userData.selectedStatCodeData.SubStat}</div>
                </span>
              )}
              {userData.selectedStatCodeData.AgencyName && (
                <span>
                  <p className="cs-form-label">Agency name</p>
                  <div>{userData.selectedStatCodeData.AgencyName}</div>
                </span>
              )}
            </div>
          )}
          <div className="cs-form-caption">Principal or agency manager making request*</div>
          <FieldGroup id="commission-access-fieldgroup" messages={errorMessages}>
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Your name"
              value={form?.name}
              highlightType={error && !form?.name ? 'error' : undefined}
              onChange={(event) => onNameChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Your role or title"
              value={form?.role}
              highlightType={error && !form?.role ? 'error' : undefined}
              onChange={(event) => onRoleChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Your phone number"
              mask="phone"
              value={form?.phone}
              highlightType={error && !form?.phone ? 'error' : undefined}
              onChange={(event) => onPhoneChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Your email address"
              value={form?.email}
              highlightType={error && !form?.email ? 'error' : undefined}
              onChange={(event) => onEmailChange(event)}
            />
          </FieldGroup>
          <Textarea
            labelVisual="Special instructions (optional)"
            value={form?.instructions}
            onChange={(event) => setForm((form) => ({ ...form, instructions: event.target?.value }))}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          ></Textarea>
          <Heading type="h5-medium" className="cs-subtext">
            Individuals to be granted access to direct bill commissions
          </Heading>
          <FieldGroup messages={moreErrorMessages} id="additional-users-fieldgroup">
            {form?.additional_access &&
              form?.additional_access.map((x, i) => (
                <div className="add-another-input-row">
                  <AlphanumericInput
                    id="Alphanumeric--default"
                    labelVisual="Name"
                    value={form.additional_access[i]?.name}
                    onChange={(event) =>
                      setForm((form) => ({
                        ...form,
                        additional_access: form.additional_access.map((item, index) =>
                          index == i ? { ...form.additional_access[i], name: event.currentTarget?.value } : item,
                        ),
                      }))
                    }
                    highlightType={
                      moreErrors &&
                      (form.additional_access[i]?.name == '' || form.additional_access[i]?.name == undefined)
                        ? 'error'
                        : undefined
                    }
                    className="additional-user-name-field"
                  />
                  <AlphanumericInput
                    id="Alphanumeric--default"
                    labelVisual="User ID"
                    value={form.additional_access[i]?.userid}
                    onChange={(event) =>
                      setForm((form) => ({
                        ...form,
                        additional_access: form.additional_access.map((item, index) =>
                          index == i ? { ...form.additional_access[i], userid: event.currentTarget?.value } : item,
                        ),
                      }))
                    }
                    highlightType={
                      moreErrors &&
                      (form.additional_access[i]?.userid == '' || form.additional_access[i]?.userid == undefined)
                        ? 'error'
                        : undefined
                    }
                    className="additional-user-userid-field"
                  />
                  <AlphanumericInput
                    id="Alphanumeric--default"
                    labelVisual="Email address"
                    value={form.additional_access[i]?.email}
                    onChange={(event) =>
                      setForm((form) => ({
                        ...form,
                        additional_access: form.additional_access.map((item, index) =>
                          index == i ? { ...form.additional_access[i], email: event.currentTarget?.value } : item,
                        ),
                      }))
                    }
                    highlightType={
                      moreErrors &&
                      (form.additional_access[i]?.email == '' || form.additional_access[i]?.email == undefined)
                        ? 'error'
                        : undefined
                    }
                    className="additional-user-email-field"
                  />
                  {i > 0 && (
                    <div
                      className="add-another-link"
                      onClick={() =>
                        setForm((form) => ({
                          ...form,
                          additional_access: form?.additional_access?.filter((item, index) => index !== i),
                        }))
                      }
                    >
                      <IconRemoveItem size="16" /> <span>Remove</span>
                    </div>
                  )}
                </div>
              ))}
          </FieldGroup>
          <div
            className="add-another-link"
            onClick={() =>
              setForm((form) => ({ ...form, additional_access: [...(form?.additional_access ?? []), {}] }))
            }
          >
            <IconAddItem size="16" /> <span>Add another</span>
          </div>
          <div className="cs-bottom-row">
            <div>
              <p>Requests may take up to ten days. Individuals will be notified once access has been granted.</p>
              <p>*Only requests from principals or agency managers will be considered</p>
            </div>
            <Button
              className="mail-to-link"
              loading={sending}
              variant="primary"
              disabled={sent}
              onClick={() => requestAccess()}
            >
              {sent ? 'Access Requested' : 'Request Access'}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default CommissionAccess;
